import { useQuery } from "@tanstack/react-query";
import React from "react";
import { INTERVAL } from "@coworker/app/src/constants/clientTime";
import { getMsalInstanceAsync } from "@coworker/app/src/core/auth/useAuth";
import { AccountInfo } from "@azure/msal-browser";

interface GraphUserProfile {
  jobTitle: string | null;
  department: string | null;
  officeLocation: string | null;
  businessPhones: string[];
  usageLocation: string | null;
  companyName: string | null;
}

async function acquireDelegatedGraphToken(): Promise<string> {
  const msalInstance = await getMsalInstanceAsync();
  const activeAccount = msalInstance.getActiveAccount();
  const fallbackAccount = msalInstance.getAllAccounts()[0];
  const account = (activeAccount ?? fallbackAccount) as AccountInfo | undefined;
  if (!account) {
    throw new Error("No MSAL account available for Graph token acquisition");
  }
  const response = await msalInstance.acquireTokenSilent({
    scopes: ["User.Read"],
    account,
  });
  return response.accessToken;
}

async function fetchGraphUserProfile(): Promise<GraphUserProfile> {
  const token = await acquireDelegatedGraphToken();

  const response = await fetch(
    "https://graph.microsoft.com/v1.0/me?$select=jobTitle,department,officeLocation,businessPhones,usageLocation,companyName",
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to fetch Graph user profile (${response.status}): ${response.statusText}`
    );
  }

  return response.json() as Promise<GraphUserProfile>;
}

export function useGraphUserProfile() {
  const accountId = useActiveAccountId();

  return useQuery({
    queryKey: ["graphUserProfile", accountId],
    queryFn: fetchGraphUserProfile,
    enabled: !!accountId,
    staleTime: INTERVAL.HOUR,
    refetchOnWindowFocus: false,
  });
}

function useActiveAccountId(): string | undefined {
  const [accountId, setAccountId] = React.useState<string | undefined>();

  React.useEffect(() => {
    getMsalInstanceAsync().then((msalInstance) => {
      const account =
        msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
      setAccountId(account?.homeAccountId);
    });
  }, []);

  return accountId;
}
